/**
 * Theme 1–10 Background Overlay (Figma design)
 * Renders orbs at corners, diagonal lines, and star dots.
 * Colors per theme: 1 cyan/magenta, 2 orange/red, 3 gold, 4 purple, 5 gray, 6 magenta, 7 teal, 8 cyan, 9 yellow, 10 violet.
 */

import React, { useMemo } from 'react';
import { useVFXConfigContext } from '@/features/vfx/contexts/VFXConfigContext';
import { THEME_NUMBER } from '@/config/appTheme';

function useCurrentBackgroundTheme(): number {
  const { state } = useVFXConfigContext();
  return useMemo(() => {
    let themeNumber: number | undefined;
    if (state.config?.app_themes !== undefined && state.config.app_themes !== null) {
      const parsed = parseInt(String(state.config.app_themes), 10);
      if (!isNaN(parsed)) themeNumber = parsed;
    }
    if (themeNumber === undefined && state.config?.background?.theme !== undefined && state.config.background.theme !== null) {
      const t = Number(state.config.background.theme);
      if (!isNaN(t) && t >= 0) themeNumber = t;
    }
    if (themeNumber === undefined || themeNumber === 0) themeNumber = THEME_NUMBER;
    return themeNumber ?? 1;
  }, [state.config?.app_themes, state.config?.background?.theme]);
}

const DOTS_POSITIONS: { top: string; left: string }[] = [
  { top: '12%', left: '18%' },
  { top: '25%', left: '75%' },
  { top: '40%', left: '45%' },
  { top: '60%', left: '25%' },
  { top: '75%', left: '60%' },
  { top: '85%', left: '35%' },
  { top: '20%', left: '50%' },
  { top: '55%', left: '80%' },
];

/** Theme 1–10: overlay colors per theme (orbs, lines) */
const THEME_OVERLAY_COLORS: Record<
  number,
  { orb1: string; orb2: string; line1: string; line1Shadow: string; line2: string; line2Shadow: string }
> = {
  1: {
    orb1: 'radial-gradient(circle, rgba(34, 211, 238, 0.4) 0%, rgba(34, 211, 238, 0.12) 40%, transparent 70%)',
    orb2: 'radial-gradient(circle, rgba(236, 72, 153, 0.4) 0%, rgba(236, 72, 153, 0.12) 40%, transparent 70%)',
    line1: 'linear-gradient(90deg, transparent 0%, rgba(34, 211, 238, 0.25) 30%, rgba(34, 211, 238, 0.4) 50%, rgba(34, 211, 238, 0.25) 70%, transparent 100%)',
    line1Shadow: '0 0 20px rgba(34, 211, 238, 0.3)',
    line2: 'linear-gradient(90deg, transparent 0%, rgba(236, 72, 153, 0.2) 35%, rgba(236, 72, 153, 0.35) 55%, rgba(236, 72, 153, 0.2) 75%, transparent 100%)',
    line2Shadow: '0 0 20px rgba(236, 72, 153, 0.25)',
  },
  2: {
    orb1: 'radial-gradient(circle, rgba(237, 103, 31, 0.4) 0%, rgba(237, 103, 31, 0.12) 40%, transparent 70%)',
    orb2: 'radial-gradient(circle, rgba(139, 39, 2, 0.45) 0%, rgba(39, 2, 2, 0.15) 40%, transparent 70%)',
    line1: 'linear-gradient(90deg, transparent 0%, rgba(237, 103, 31, 0.25) 30%, rgba(237, 103, 31, 0.4) 50%, rgba(237, 103, 31, 0.25) 70%, transparent 100%)',
    line1Shadow: '0 0 20px rgba(237, 103, 31, 0.3)',
    line2: 'linear-gradient(90deg, transparent 0%, rgba(180, 50, 20, 0.2) 35%, rgba(180, 50, 20, 0.35) 55%, rgba(180, 50, 20, 0.2) 75%, transparent 100%)',
    line2Shadow: '0 0 20px rgba(180, 50, 20, 0.25)',
  },
  3: {
    orb1: 'radial-gradient(circle, rgba(86, 70, 6, 0.45) 0%, rgba(86, 70, 6, 0.14) 40%, transparent 70%)',
    orb2: 'radial-gradient(circle, rgba(24, 19, 1, 0.5) 0%, rgba(24, 19, 1, 0.18) 40%, transparent 70%)',
    line1: 'linear-gradient(90deg, transparent 0%, rgba(86, 70, 6, 0.3) 30%, rgba(120, 98, 10, 0.4) 50%, rgba(86, 70, 6, 0.3) 70%, transparent 100%)',
    line1Shadow: '0 0 20px rgba(86, 70, 6, 0.35)',
    line2: 'linear-gradient(90deg, transparent 0%, rgba(60, 50, 5, 0.2) 35%, rgba(80, 65, 8, 0.35) 55%, rgba(60, 50, 5, 0.2) 75%, transparent 100%)',
    line2Shadow: '0 0 20px rgba(80, 65, 8, 0.3)',
  },
  4: {
    orb1: 'radial-gradient(circle, rgba(116, 0, 204, 0.4) 0%, rgba(116, 0, 204, 0.12) 40%, transparent 70%)',
    orb2: 'radial-gradient(circle, rgba(40, 4, 67, 0.45) 0%, rgba(40, 4, 67, 0.15) 40%, transparent 70%)',
    line1: 'linear-gradient(90deg, transparent 0%, rgba(116, 0, 204, 0.25) 30%, rgba(116, 0, 204, 0.4) 50%, rgba(116, 0, 204, 0.25) 70%, transparent 100%)',
    line1Shadow: '0 0 20px rgba(116, 0, 204, 0.3)',
    line2: 'linear-gradient(90deg, transparent 0%, rgba(80, 20, 140, 0.2) 35%, rgba(90, 30, 160, 0.35) 55%, rgba(80, 20, 140, 0.2) 75%, transparent 100%)',
    line2Shadow: '0 0 20px rgba(90, 30, 160, 0.25)',
  },
  5: {
    orb1: 'radial-gradient(circle, rgba(143, 147, 160, 0.4) 0%, rgba(143, 147, 160, 0.12) 40%, transparent 70%)',
    orb2: 'radial-gradient(circle, rgba(55, 56, 60, 0.45) 0%, rgba(21, 21, 21, 0.2) 40%, transparent 70%)',
    line1: 'linear-gradient(90deg, transparent 0%, rgba(143, 147, 160, 0.25) 30%, rgba(143, 147, 160, 0.4) 50%, rgba(143, 147, 160, 0.25) 70%, transparent 100%)',
    line1Shadow: '0 0 20px rgba(143, 147, 160, 0.3)',
    line2: 'linear-gradient(90deg, transparent 0%, rgba(100, 102, 110, 0.2) 35%, rgba(120, 122, 132, 0.35) 55%, rgba(100, 102, 110, 0.2) 75%, transparent 100%)',
    line2Shadow: '0 0 20px rgba(120, 122, 132, 0.25)',
  },
  6: {
    orb1: 'radial-gradient(circle, rgba(177, 0, 204, 0.4) 0%, rgba(177, 0, 204, 0.12) 40%, transparent 70%)',
    orb2: 'radial-gradient(circle, rgba(58, 1, 67, 0.45) 0%, rgba(58, 1, 67, 0.15) 40%, transparent 70%)',
    line1: 'linear-gradient(90deg, transparent 0%, rgba(177, 0, 204, 0.25) 30%, rgba(177, 0, 204, 0.4) 50%, rgba(177, 0, 204, 0.25) 70%, transparent 100%)',
    line1Shadow: '0 0 20px rgba(177, 0, 204, 0.3)',
    line2: 'linear-gradient(90deg, transparent 0%, rgba(140, 0, 160, 0.2) 35%, rgba(160, 20, 180, 0.35) 55%, rgba(140, 0, 160, 0.2) 75%, transparent 100%)',
    line2Shadow: '0 0 20px rgba(160, 20, 180, 0.25)',
  },
  7: {
    orb1: 'radial-gradient(circle, rgba(0, 204, 150, 0.4) 0%, rgba(0, 204, 150, 0.12) 40%, transparent 70%)',
    orb2: 'radial-gradient(circle, rgba(0, 55, 41, 0.45) 0%, rgba(0, 55, 41, 0.18) 40%, transparent 70%)',
    line1: 'linear-gradient(90deg, transparent 0%, rgba(0, 204, 150, 0.25) 30%, rgba(0, 204, 150, 0.4) 50%, rgba(0, 204, 150, 0.25) 70%, transparent 100%)',
    line1Shadow: '0 0 20px rgba(0, 204, 150, 0.3)',
    line2: 'linear-gradient(90deg, transparent 0%, rgba(0, 160, 120, 0.2) 35%, rgba(0, 180, 135, 0.35) 55%, rgba(0, 160, 120, 0.2) 75%, transparent 100%)',
    line2Shadow: '0 0 20px rgba(0, 180, 135, 0.25)',
  },
  8: {
    orb1: 'radial-gradient(circle, rgba(13, 218, 241, 0.4) 0%, rgba(13, 218, 241, 0.12) 40%, transparent 70%)',
    orb2: 'radial-gradient(circle, rgba(1, 45, 50, 0.45) 0%, rgba(1, 45, 50, 0.18) 40%, transparent 70%)',
    line1: 'linear-gradient(90deg, transparent 0%, rgba(13, 218, 241, 0.25) 30%, rgba(13, 218, 241, 0.4) 50%, rgba(13, 218, 241, 0.25) 70%, transparent 100%)',
    line1Shadow: '0 0 20px rgba(13, 218, 241, 0.3)',
    line2: 'linear-gradient(90deg, transparent 0%, rgba(10, 180, 200, 0.2) 35%, rgba(15, 200, 220, 0.35) 55%, rgba(10, 180, 200, 0.2) 75%, transparent 100%)',
    line2Shadow: '0 0 20px rgba(15, 200, 220, 0.25)',
  },
  9: {
    orb1: 'radial-gradient(circle, rgba(255, 237, 74, 0.4) 0%, rgba(255, 237, 74, 0.14) 40%, transparent 70%)',
    orb2: 'radial-gradient(circle, rgba(61, 55, 2, 0.5) 0%, rgba(61, 55, 2, 0.2) 40%, transparent 70%)',
    line1: 'linear-gradient(90deg, transparent 0%, rgba(255, 237, 74, 0.3) 30%, rgba(255, 237, 74, 0.45) 50%, rgba(255, 237, 74, 0.3) 70%, transparent 100%)',
    line1Shadow: '0 0 20px rgba(255, 237, 74, 0.35)',
    line2: 'linear-gradient(90deg, transparent 0%, rgba(220, 200, 50, 0.2) 35%, rgba(240, 220, 60, 0.35) 55%, rgba(220, 200, 50, 0.2) 75%, transparent 100%)',
    line2Shadow: '0 0 20px rgba(240, 220, 60, 0.3)',
  },
  10: {
    orb1: 'radial-gradient(circle, rgba(147, 23, 230, 0.4) 0%, rgba(147, 23, 230, 0.12) 40%, transparent 70%)',
    orb2: 'radial-gradient(circle, rgba(55, 5, 88, 0.45) 0%, rgba(55, 5, 88, 0.18) 40%, transparent 70%)',
    line1: 'linear-gradient(90deg, transparent 0%, rgba(147, 23, 230, 0.25) 30%, rgba(147, 23, 230, 0.4) 50%, rgba(147, 23, 230, 0.25) 70%, transparent 100%)',
    line1Shadow: '0 0 20px rgba(147, 23, 230, 0.3)',
    line2: 'linear-gradient(90deg, transparent 0%, rgba(177, 80, 242, 0.2) 35%, rgba(177, 80, 242, 0.35) 55%, rgba(177, 80, 242, 0.2) 75%, transparent 100%)',
    line2Shadow: '0 0 20px rgba(177, 80, 242, 0.25)',
  },
};

export const Theme1BackgroundOverlay: React.FC = () => {
  const themeNumber = useCurrentBackgroundTheme();
  const colors = THEME_OVERLAY_COLORS[themeNumber];

  if (!colors) {
    return null;
  }

  return (
    <div
      className="fixed inset-0 pointer-events-none overflow-visible"
      style={{ zIndex: 0 }}
      aria-hidden="true"
    >
      {/* Orb - top left (center at corner so glow stays inside viewport, no clipping on mobile) */}
      <div
        className="absolute rounded-full"
        style={{
          width: 'clamp(280px, 95vw, 600px)',
          height: 'clamp(280px, 95vw, 600px)',
          top: 0,
          left: 0,
          transform: 'translate(-50%, -50%)',
          background: colors.orb1,
          filter: 'blur(36px)',
        }}
      />
      {/* Orb - bottom right */}
      <div
        className="absolute rounded-full"
        style={{
          width: 'clamp(280px, 90vw, 500px)',
          height: 'clamp(280px, 90vw, 500px)',
          bottom: 0,
          right: 0,
          top: 'auto',
          left: 'auto',
          transform: 'translate(50%, 50%)',
          background: colors.orb2,
          filter: 'blur(36px)',
        }}
      />
      {/* Diagonal line 1 */}
      <div
        className="absolute w-full h-px"
        style={{
          top: '35%',
          left: '-10%',
          width: '120%',
          transform: 'rotate(-25deg)',
          background: colors.line1,
          boxShadow: colors.line1Shadow,
        }}
      />
      {/* Diagonal line 2 */}
      <div
        className="absolute w-full h-px"
        style={{
          top: '45%',
          left: '-10%',
          width: '120%',
          transform: 'rotate(-20deg)',
          background: colors.line2,
          boxShadow: colors.line2Shadow,
        }}
      />
      {/* Small star-like dots */}
      {DOTS_POSITIONS.map((pos, i) => (
        <div
          key={i}
          className="absolute rounded-full bg-white"
          style={{
            ...pos,
            width: 2,
            height: 2,
            opacity: 0.4 + (i % 3) * 0.2,
            boxShadow: '0 0 6px rgba(255, 255, 255, 0.6)',
          }}
        />
      ))}
    </div>
  );
};

export default Theme1BackgroundOverlay;
